import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Text } from "components/base/Form";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AI_ASK_QUESTION, FETCH_AI_CAPABILITIES } from "./graphql";

const AskPage = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(FETCH_AI_CAPABILITIES);
  const [question, setQuestion] = useState("");
  const [submitError, setSubmitError] = useState(null);
  const askAbortControllerRef = useRef(null);

  const [ask, askRes] = useMutation(AI_ASK_QUESTION, {
    onCompleted: (data) => {
      const res = data.aiAskQuestion;
      if (res.conversationId) {
        navigate(`/ai/ask/${res.conversationId}`);
      }
    },
    onError: (error) => {
      if (error?.networkError?.name === "AbortError") return;
      setSubmitError(error.message);
    },
  });

  const handleAsk = () => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || askRes.loading) return;
    setSubmitError(null);

    const abortController = new AbortController();
    askAbortControllerRef.current = abortController;

    ask({
      variables: {
        question: trimmedQuestion,
      },
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
    }).finally(() => {
      if (askAbortControllerRef.current === abortController) {
        askAbortControllerRef.current = null;
      }
    });
  };

  const handleStop = () => {
    askAbortControllerRef.current?.abort();
    askAbortControllerRef.current = null;
  };

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const { capabilities, exampleQuestions } = data.aiGetCapabilities;

  return (
    <Page
      className="font-mono leading-relaxed h-screen"
      title={
        <div>
          <span>Mr Bob</span>
          <span className="text-xs ml-2  font-thin">Testing</span>
        </div>
      }
      rightButtons={
        <div>
          <Link to="/ai/ask/history">History</Link>
        </div>
      }
    >
      <div className="p-6 flex-1 overflow-auto flex flex-col space-y-8">
        <div className="overflow-auto">
          <div className="space-y-6 opacity-70 text-xs px-1">
            <div>
              <h5>Introduction for Mr Bob.</h5>
              <div className="mt-2">
                Mr Bob now uses only the Odoo SQL agent to answer data
                questions from our database.
              </div>
            </div>

            <div>
              <h5>Capabilities</h5>
              <div className="mt-2">
                <p>{capabilities}</p>
                <p>
                  Responses are now faster and simpler: query rows are returned
                  directly without LLM post-processing, which reduces latency,
                  token usage, and data exposure risk.
                </p>
              </div>
            </div>

            <div>
              <h5>Example Questions</h5>
              <div className="mt-2">
                {exampleQuestions.map((q, i) => (
                  <div key={i} className="mb-2">
                    {q}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          <Text
            placeholder="Ask Anything..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
            disabled={askRes.loading}
          />
          {submitError ? (
            <div className="text-red-500 mt-2 text-xs">{submitError}</div>
          ) : null}
          <div className="flex mt-4 space-x-4">
            <Button primary onClick={handleAsk} loading={askRes.loading}>
              {askRes.loading ? "Thinking..." : "Ask Bob"}
            </Button>
            {askRes.loading ? (
              <Button danger onClick={handleStop}>
                Stop
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default AskPage;
